import { useState } from "react"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import { useEnv } from "contexts/Env"

import getMediaQuery from "css/breakpoints"
import { body50, headline50, legend100 } from "css/text"

import { getPriceDatas } from "components/price/utils"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import Button from "components/button/Button"
import Link from "components/link/Link"
import ModalContainer, { downloadModal } from "components/modal/ModalContainer"

const Heading = styled.h2`
  ${headline50}
  margin-block-end: 1.25rem;
`

const Paragraph = styled.div`
  margin-block-end: 2.5rem;

  > p {
    ${legend100}

    > a {
      text-decoration: none;
      color: rgb(var(--green));
    }
  }
`

const Sizes = styled.form`
  input[type="radio"] {
    width: 0;
    height: 0;
  }
`

const Size = styled.div`
  display: block;
  margin-block: 0.5rem;
  &:first-of-type {
    margin-block-start: 0;
  }
  &:nth-child(3) {
    margin-block-end: 0;
  }
`

const SizeLabel = styled.label`
  ${body50}
  cursor: pointer;

  display: inline-grid;
  grid-template-columns: min-content auto;
  align-items: center;

  &::before {
    content: "";
    display: inline-block;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: 1px solid rgb(var(--green));
    margin-inline-end: 0.5rem;
    vertical-align: middle;
  }
`

const SizeInput = styled.input`
  opacity: 0;
  pointer-events: none;

  :checked + label::before {
    background-color: rgb(var(--green));
  }

  :checked + label,
  :hover + label {
    color: rgb(var(--green));
  }

  &.focus-visible:focus-visible + label {
    outline: 2px solid rgb(var(--green));
  }
`

const CTAS = styled.div`
  margin-block-start: 2.5rem;

  display: inline-flex;
  flex-wrap: wrap;

  gap: 1rem;

  ${getMediaQuery("m")} {
    gap: 1rem;
  }

  > * {
    ${getMediaQuery("m")} {
      width: auto !important;
    }
  }
`

const Download = styled(Link)`
  width: 100% !important;
  cursor: pointer;

  svg {
    padding-inline-start: 0.5rem;
  }

  ${getMediaQuery("m")} {
    margin-inline-end: 1rem;
    width: auto !important;
  }
`

const Decline = styled(Button)`
  width: 100% !important;
  cursor: pointer;

  svg {
    display: none;
  }

  ${getMediaQuery("m")} {
    width: auto !important;
  }
`

const AriaHelper = styled.div`
  div:not(:focus):not(:active) {
    position: absolute !important;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
  }
`

export async function getModelData({ rmc, locale, env }) {
  return Promise.resolve(
    new URL(`/api/catalog/watches/${rmc}?language=${locale.current.codes.www}`, process.browser ? env.services : process.env.SERVICE_CATALOG_URL)
  )
    .then(url => new Request(url))
    .then(request => fetch(request))
    .then(response => response.json())
    .catch(err => err)
}

export default function DownloadModal({ onClose, title, paragraph, sizes, cta, cta_decline: ctaDecline, path, rmc, downloadType }) {
  const console = useConsole()

  const locale = useLocale()
  const env = useEnv()
  const { countryCode } = useUser()

  const dictionary = useDictionary()

  const [checkedSize, setCheckedSize] = useState(sizes[0] ? sizes[0].size_tch : null)

  const onkeydown = e => {
    if (e.key === "Enter") onClose()
  }

  const onCheck = e => {
    if (e.target.checked) {
      setCheckedSize(e.target.value)
    }
  }

  async function triggerEvent() {
    if (downloadType === "wallpaper") {
      const modelData = await getModelData({ rmc, locale, env })
      const { price, currency } = await getPriceDatas({ rmc, countryCode, locale, env })

      waTriggerEvent({
        eventName: "download",
        eventType: "Click",
        products: [
          {
            productCategory: modelData.familyNode.code,
            productModel: modelData.nameCode,
            productID: rmc,
            productQuantity: "1",
            productPrice: price,
            currency: currency,
          },
        ],
        downloadInfo: {
          position: window.location.pathname.includes("/media") ? "media page" : "model page",
          type: downloadType.toLowerCase(),
          fileName: path,
          size: checkedSize,
        },
      })
    }
  }

  console.verbose("Modal(%o")
  return (
    <ModalContainer onClose={onClose} className={downloadModal} modalHeading='modal-heading'>
      <Heading id='modal-heading' dangerouslySetInnerHTML={{ __html: title }} />
      <Paragraph dangerouslySetInnerHTML={{ __html: paragraph }} />
      <Sizes>
        {sizes?.map(size => {
          return (
            <Size key={size.size_tch}>
              <SizeInput
                checked={checkedSize === size.size_tch}
                type='radio'
                name='size'
                id={`size-${size.size_tch}`}
                value={size.size_tch}
                onChange={onCheck}
              />
              <SizeLabel htmlFor={`size-${size.size_tch}`}>{size.label}</SizeLabel>
            </Size>
          )
        })}
        <CTAS>
          <Download
            {...cta}
            className={cta?.style}
            external={downloadType === "userGuide" ? path : `${path}_${checkedSize}.jpg?imformat=generic`}
            onClick={triggerEvent}
          >
            {cta?.label}
            {/* a11y  */}
            <AriaHelper>
              <div>{dictionary.newWindow()}</div>
            </AriaHelper>
          </Download>
          <Decline {...ctaDecline} aria_label={ctaDecline?.label} className={ctaDecline?.style} onClick={onClose} onKeyDown={onkeydown} icon='none'>
            {ctaDecline?.label}
          </Decline>
        </CTAS>
      </Sizes>
    </ModalContainer>
  )
}
